'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { showsClient, type ShowListRecord, type ShowsSearchResponse } from '@bip/api-client';
import { ShowCard } from './ShowCard';
import { ShowCardSkeleton } from './Skeletons';
import { ShowsFilters } from './ShowsFilters';
import { getStoredCity, replaceShowsSearchParams, useShowsQueryParams } from '../_lib/useShowsQueryParams';
import { browserTimeZone } from '../config';
import { getEffectiveBookingDate } from '../_lib/dateUtils';

interface ShowsListProps {
  initialData: ShowsSearchResponse;
}


/**
 * Listing/home page results — forks HotelsList.tsx's `IntersectionObserver`
 * infinite-scroll pattern (DOCS/shows/plan.md §5.1: "canonical, race-
 * condition-tested — do NOT reimplement the legacy scroll-position-math
 * approach"). Filters/sort live entirely in the URL (root CLAUDE.md
 * back-navigation rule); this component reads `useShowsQueryParams` (NOT
 * `next/navigation`'s `useSearchParams` directly — see that hook's own doc
 * comment for why) and refetches page 1 whenever it changes.
 */
function mapCmsEventToRecord(event: any): ShowListRecord {
  const slug = event.Page_Url ? event.Page_Url.split('/').pop()! : String(event.EventId || event._id);
  return {
    id: String(event.EventId || event._id),
    mongoId: event._id,
    slug,
    name: event.Name || '',
    category: event.EventType?.CategoryName || 'Musical',
    description: event.TagLine || null,
    tagLine: event.TagLine || null,
    image: event.MainImageUrl || event.SmallImageUrl || null,
    rating: event.ReviewData?.AverageRating ?? null,
    reviewCount: event.ReviewData?.ReviewsCount ?? null,
    price: event.CurrentPrice ?? event.finalPriceWithAllCommission ?? event.EventMinimumPrice ?? 0,
    currency: event.userCurrency || 'GBP',
    currencySymbol: event.userCurrencySymbol || '£',
    discountPercent: null,
    offerLabel: event.OfferLabel || null,
    venueName: event.Venue?.theatreName || null,
    city: event.Venue?.city || null,
    state: event.Venue?.state || null,
    startDate: event.StartDate || '',
    endDate: event.EndDate || '',
  };
}

async function fetchInitiateTheatreSearch(searchParams: URLSearchParams, page = 1, limit = 20): Promise<ShowsSearchResponse> {
  const category = searchParams.get('category');
  const rating = searchParams.get('rating');
  const priceMin = searchParams.get('priceMin');
  const priceMax = searchParams.get('priceMax');
  const q = searchParams.get('q');
  const sortBy = searchParams.get('sortBy');

  const city = searchParams.get('city') || undefined;
  const cityId = searchParams.get('cityId') || undefined;

  const dateFrom = searchParams.get('dateFrom') || undefined;
  const dateTo = searchParams.get('dateTo') || undefined;
  const today = searchParams.get('today') === 'true' ? true : undefined;
  const weekend = searchParams.get('weekend') === 'true' ? true : undefined;

  const filterParam: Record<string, any> = {};
  if (category) filterParam.category = category.split(',').map((c) => c.trim());
  if (rating) {
    filterParam.rating = rating
      .split(',')
      .map((r) => Number(r.trim()))
      .filter((n) => Number.isFinite(n));
  }
  if (priceMin || priceMax) {
    filterParam.price = {
      min: priceMin ? Number(priceMin) : undefined,
      max: priceMax ? Number(priceMax) : undefined,
    };
  }
  if (dateFrom) {
    filterParam.performanceDate = {
      from: dateFrom,
      to: dateTo || dateFrom,
    };
  }
  if (today) filterParam.today = true;
  if (weekend) filterParam.weekend = true;

  const sortParam: Record<string, boolean> = {};
  if (sortBy === 'price') sortParam.SortFilter_priceLowToHigh = true;
  if (sortBy === 'rating') sortParam.SortFilter_rating = true;
  if (sortBy === 'newest') sortParam.SortFilter_newest = true;

  const payload: Record<string, any> = {
    eventSearch: q || '',
    filterParam,
    sortParam,
    page,
    timezone: browserTimeZone(),
  };
  if (cityId) payload.cityId = cityId;
  if (city) payload.city = city;
  if (dateFrom) payload.dateFrom = dateFrom;
  if (dateTo) payload.dateTo = dateTo;
  if (today) payload.today = true;
  if (weekend) payload.weekend = true;

  const data = await showsClient.initiateTheatreSearch(payload, limit);
  const records = (data.eventListdata || []).map(mapCmsEventToRecord);
  const total = data.pagination?.total ?? records.length;
  const categories = (data.filters?.categories || []).map((c) => ({
    value: c.categoryName,
    count: c.count,
  }));
  const ratingThresholds = (data.filters?.ratingFilter || []).map((r) => ({
    value: r.rating,
    count: r.count,
  }));
  const priceBounds = {
    min: data.filters?.priceFilter?.minAvailable ?? 0,
    max: data.filters?.priceFilter?.maxAvailable ?? 0,
  };

  return {
    records,
    page: data.pagination?.page ?? page,
    limit: data.pagination?.limit ?? limit,
    total,
    totalPages: Math.ceil(total / limit),
    facets: {
      categories,
      ratingThresholds,
      priceBounds,
    },
    resultsHeading: `${searchParams.get('city') || data.data?.city || 'London'} Theaters - ${total} shows found`,
    sortBy: data.sortApplied || 'recommended',
  };
}

export function ShowsList({ initialData }: ShowsListProps) {
  const searchParams = useShowsQueryParams();

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search) {
      const isReload =
        performance.navigation.type === 1 ||
        (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type === 'reload';

      if (isReload) {
        replaceShowsSearchParams(window.location.pathname, new URLSearchParams());
      }
    }
  }, []);

  const [records, setRecords] = useState<ShowListRecord[]>(initialData.records);

  useEffect(() => {
    // If no city in URL, but a stored city exists and is not London:
    // clear records and set loading immediately so London shows don't flash
    if (!searchParams.get('city')) {
      const storedCity = getStoredCity();
      if (storedCity && storedCity.toLowerCase() !== 'london') {
        if (records.length > 0) {
          lastCountRef.current = Math.min(20, Math.max(8, records.length));
        }
        setIsLoading(true);
        setRecords([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [page, setPage] = useState(initialData.page);
  const [hasMore, setHasMore] = useState(initialData.page < initialData.totalPages);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [total, setTotal] = useState(initialData.total);
  const [facets, setFacets] = useState(initialData.facets);
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onSearchStart() {
      if (records.length > 0) {
        lastCountRef.current = Math.min(20, Math.max(8, records.length));
      }
      setIsLoading(true);
      setRecords([]);
      setSearchTrigger((k) => k + 1);
    }
    window.addEventListener('bip-shows-search-start', onSearchStart);
    return () => window.removeEventListener('bip-shows-search-start', onSearchStart);
  }, [records]);

  const lastProcessedRef = useRef({ params: searchParams.toString(), retryKey: 0, searchTrigger: 0 });
  const lastCountRef = useRef(Math.min(20, Math.max(8, initialData.records.length)));

  useEffect(() => {
    const currentParams = searchParams.toString();
    const paramsChanged = currentParams !== lastProcessedRef.current.params;
    const retryTriggered = retryKey !== lastProcessedRef.current.retryKey;
    const searchTriggered = searchTrigger !== lastProcessedRef.current.searchTrigger;
    lastProcessedRef.current = { params: currentParams, retryKey, searchTrigger };
    if (!paramsChanged && !retryTriggered && !searchTriggered) return;

    let cancelled = false;

    if (records.length > 0) lastCountRef.current = Math.min(20, Math.max(8, records.length));
    setIsLoading(true);
    setSearchError(false);
    setRecords([]);

    fetchInitiateTheatreSearch(searchParams, 1, 20)
      .then((result) => {
        if (cancelled) return;
        setRecords(result.records);
        setPage(result.page);
        setHasMore(result.page < result.totalPages);
        setTotal(result.total);
        setFacets(result.facets);
      })
      .catch(() => {
        if (cancelled) return;
        setSearchError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, retryKey, searchTrigger]);

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const pageRef = useRef(page);
  pageRef.current = page;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const loadMore = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreRef.current) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    try {
      const nextPage = pageRef.current + 1;
      const result = await fetchInitiateTheatreSearch(searchParamsRef.current, nextPage, 20);
      setRecords((prev) => [...prev, ...result.records]);
      setPage(nextPage);
      pageRef.current = nextPage;
      const more = nextPage < result.totalPages && result.records.length > 0;
      setHasMore(more);
      hasMoreRef.current = more;
    } catch (err) {
      console.error('Error loading more shows:', err);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  // 1. IntersectionObserver for infinite scrolling with prefetch margin
  useEffect(() => {
    const el = observerTarget.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMoreRef.current && !isLoadingRef.current) {
          loadMore();
        }
      },
      { rootMargin: '300px', threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  // 2. Window scroll event listener for guaranteed parity with CMS infinite-scroll
  useEffect(() => {
    let scrollTimeout: ReturnType<typeof setTimeout> | undefined;
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (isLoadingRef.current || !hasMoreRef.current) return;
        const scrollPosition = window.scrollY + window.innerHeight;
        const threshold = document.documentElement.scrollHeight - 600;
        if (scrollPosition >= threshold) {
          loadMore();
        }
      }, 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [loadMore]);

  // "{City} Theaters - {N} shows found[ for "{query}"]" — city defaults to
  // "London" when no city has been searched/filtered (matching the app's
  // own default-city convention elsewhere, e.g. ShowsHero's search bar).
  const headingCity = searchParams.get('city') || 'London';
  const headingQuery = searchParams.get('q')?.trim();
  const displayHeading = headingQuery
    ? `${headingCity} Theaters - ${total} shows found for "${headingQuery}"`
    : `${headingCity} Theaters - ${total} shows found`;

  // Heading shows only when actual filters (other than city, page, or sortBy) are applied.
  const hasActiveFilters = Array.from(searchParams.keys()).some(
    (key) => key !== 'city' && key !== 'page' && key !== 'sortBy'
  );


  const effectiveBookingDate = getEffectiveBookingDate(searchParams);

  return (
    <>
      {/* DOCS/shows/image copy.png's exact sequence: filter buttons row,
          THEN this heading, THEN the active-filter chips + Reset row — the
          heading is passed AS A PROP so `ShowsFilters` can render it
          between those two rows (both live inside that one component). */}
      <ShowsFilters
        facets={facets}
        disabled={!isLoading && records.length === 0}
        resultsHeading={
          hasActiveFilters && records.length > 0 ? (
            <h2 className="listing-heading text-lg font-bold text-secondary">{displayHeading}</h2>
          ) : null
        }
      />

      {records.length === 0 && !isLoading && searchError && (
        <div className="py-16 text-center">
          <i className="fas fa-exclamation-circle mb-4 block text-5xl text-orange" />
          <h3 className="mb-2 text-lg font-bold text-secondary">Search failed</h3>
          <p className="mb-4 text-sm text-gray-text">The service may be starting up. Please try again.</p>
          <button
            type="button"
            onClick={() => setRetryKey((k) => k + 1)}
            className="rounded-xl bg-primary px-6 py-2.5 text-sm font-bold text-white"
          >
            Try Again
          </button>
        </div>
      )}
      {/* "No Shows Available" — general empty state for every sort/filter combination except By
          Discount's own, more specific message. Explicit requirement: never show "No records
          found"/"No Record Found" anywhere on this page. */}
      {records.length === 0 && !isLoading && !searchError && (
        <div className="py-16 text-center">
          <i className="fas fa-theater-masks mb-4 block text-6xl text-info" />
          <h3 className="mb-2 text-lg font-bold text-secondary">
            {searchParams.get('sortBy') === 'discount' ? 'No events with deals available.' : 'No Shows Available'}
          </h3>
        </div>
      )}

      {records.length === 0 && isLoading ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 mb-6 pb-4">
          {Array.from({ length: lastCountRef.current }).map((_, i) => (
            <ShowCardSkeleton key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mb-6 pb-4">
          {records.map((show) => (
            <ShowCard
              key={show.id}
              show={show}
              dateFrom={searchParams.get('dateFrom')}
              bookingDate={effectiveBookingDate}
            />
          ))}
          {/* Load-more pagination — existing cards stay, 4 trailing
              skeletons while the next page fetches. */}
          {isLoading && records.length > 0 && (
            <>
              <ShowCardSkeleton />
              <ShowCardSkeleton />
              <ShowCardSkeleton />
              <ShowCardSkeleton />
            </>
          )}
        </div>
      )}

      {hasMore && <div ref={observerTarget} className="py-6" style={{ minHeight: '40px' }} aria-hidden />}
    </>
  );
}

